import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { fetchVATChart } from '../api'
import type { VATChartEntry } from '../types'
import { Spinner, CategoryTag } from '../components/UI'

const FILTERS = [
  { key: 'all',              label: 'All Countries' },
  { key: 'domestic',         label: 'Domestic' },
  { key: 'foreign_noneu',    label: 'Foreign Non-EU' },
  { key: 'ineligible_expense', label: 'Ineligible' },
  { key: 'no_reciprocity',   label: 'No Reciprocity' },
  { key: 'no_vat_mechanism', label: 'No Mechanism' },
]

export default function VATChartPage() {
  const [search, setSearch]   = useState('')
  const [catFilter, setCat]   = useState('all')
  const { data: chart = [], isLoading } = useQuery({
    queryKey: ['vat-chart'],
    queryFn: fetchVATChart,
    staleTime: Infinity,
  })

  const filtered = chart.filter((c: VATChartEntry) => {
    const matchSearch = !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.iso.toLowerCase().includes(search.toLowerCase())
    const matchCat = catFilter === 'all' || c.category === catFilter
    return matchSearch && matchCat
  })

  return (
    <div>
      <div style={{ marginBottom: 26 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-1)', marginBottom: 4 }}>
          VAT Chart 2025 — Non-EU Claimants (India)
        </h1>
        <p style={{ fontSize: 13.5, color: 'var(--text-2)' }}>
          Country-wise hotel VAT rates, eligibility and claim deadlines for Indian entities
        </p>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
        {[
          ['domestic',           '#0096c7',  'Domestic (SG/JP/AU/NZ)'],
          ['foreign_noneu',      '#ea580c',  'Foreign Non-EU'],
          ['ineligible_expense', '#ef4444',  'Ineligible Expense'],
          ['no_reciprocity',     '#7c3aed',  'No Reciprocity'],
          ['no_vat_mechanism',   '#94a3b8',  'No VAT Mechanism'],
        ].map(([, color, label]) => (
          <div key={String(label)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)' }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: String(color), flexShrink: 0 }} />
            {label}
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '0 0 240px' }}>
          <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
          <input
            type="text"
            placeholder="Search country or ISO…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', paddingLeft: 30, paddingRight: 12, paddingTop: 8, paddingBottom: 8,
              background: '#fff', border: '1px solid var(--border-md)', borderRadius: 8,
              fontSize: 13, color: 'var(--text-1)', outline: 'none',
              fontFamily: 'inherit',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setCat(f.key)}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 500,
                cursor: 'pointer', border: '1px solid',
                background: catFilter === f.key ? '#0096c7' : '#fff',
                color: catFilter === f.key ? '#fff' : 'var(--text-2)',
                borderColor: catFilter === f.key ? '#0096c7' : 'var(--border-md)',
                fontFamily: 'inherit', transition: 'all .15s',
              }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><Spinner size={28} /></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead><tr>
                <th>Country</th><th>ISO</th><th>VAT System</th>
                <th style={{ textAlign: 'right' }}>Std Rate</th>
                <th style={{ textAlign: 'right' }}>Hotel Rate</th>
                <th style={{ textAlign: 'center' }}>Hotels ✓</th>
                <th>Deadline</th><th>Category</th><th>Notes</th>
              </tr></thead>
              <tbody>
                {filtered.map((c: VATChartEntry) => (
                  <tr key={c.iso}>
                    <td style={{ fontWeight: 500 }}>{c.name}</td>
                    <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{c.iso}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{c.vat_system}</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 600, color: '#0096c7' }}>{c.rate}%</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 600, color: c.hotel_rate !== c.rate ? '#ea580c' : '#0096c7' }}>
                      {c.hotel_rate > 0 ? `${c.hotel_rate}%` : '—'}
                    </td>
                    <td style={{ textAlign: 'center', fontSize: 16 }}>
                      {c.hotels_eligible
                        ? <span style={{ color: '#15803d' }}>✓</span>
                        : <span style={{ color: '#b91c1c' }}>✗</span>
                      }
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{c.deadline}</td>
                    <td><CategoryTag category={c.category} /></td>
                    <td style={{ fontSize: 11.5, color: 'var(--text-2)', maxWidth: 220 }}>{c.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-3)', background: '#f8fafc' }}>
            Showing {filtered.length} of {chart.length} countries
          </div>
        </div>
      )}
    </div>
  )
}
